// Shared widget-config + embed helpers for the marketing-site widget
// tools (builder, Instagram/hashtag feed, social-proof, link-in-bio).
//
// The Idukki widget is GUID-driven: a saved gallery-layout is rendered
// anywhere by `<div data-ugc="idukki" data-guid="...">` + the loader
// script, and all appearance comes from the saved layout (see the
// idukki-ext DisplayWidget). So the marketing site only ever needs to
// (a) let a visitor compose a layout config, (b) show a real preview of
// it, and (c) hand back the embed snippet once a guid exists.
//
// URLs are env-configurable because the loader / preview host differ
// across environments (Idukki CDN vs the Feefo white-label proxy).
// Defaults point at Idukki hosts; override only for a Feefo co-brand.

export const WIDGET_LOADER_URL =
  process.env.NEXT_PUBLIC_WIDGET_LOADER_URL ?? 'https://assets.idukki.io/version/script_0'

export const WIDGET_PREVIEW_BASE =
  process.env.NEXT_PUBLIC_WIDGET_PREVIEW_BASE ?? 'https://widget.idukki.io'

export const APP_URL = process.env.NEXT_PUBLIC_APP_URL ?? 'https://app.idukki.io'

/** The layout view types a visitor can pick in the builder. Mirrors the
 *  `galleryView` keys the widget renderer understands (see helpers/layoutMap
 *  in nextapp). Kept to the safe, broadly-supported set for the public tool. */
export type WidgetView =
  | 'grid'
  | 'carousel'
  | 'masonry'
  | 'collage'
  | 'stories'
  | 'reelCarousel'
  | 'wall2'

/** A source a visitor wants their UGC pulled from. Drives copy + the
 *  per-channel tool skins; the real connection happens in-app via OAuth. */
export type WidgetSource = 'instagram' | 'tiktok' | 'hashtag' | 'reviews' | 'youtube' | 'upload'

/** The minimal, layout-shaped config the builder composes. This maps 1:1
 *  onto the `layout.galleryInformation` the create-collection API expects,
 *  so the same object can be sent straight to `POST /gallery-layouts`. */
export type WidgetConfig = {
  name: string
  view: WidgetView
  source: WidgetSource
  /** The handle / hashtag / business the visitor typed for their source,
   *  e.g. "@yourbrand" or "#campaign". Captured for the preview + lead so
   *  the app can prefill the connection. Does not affect the layout. */
  sourceHandle?: string
  columns: number
  mobileColumns: number
  spacing: number
  pagination: 'pagination' | 'loadMore' | 'autoScroll' | 'arrows' | 'none'
  accent: string
}

export type WidgetEnginePost = Record<string, unknown>

export type WidgetEnginePayload = {
  id: string | number
  business_id: string | number
  data: WidgetEnginePost[]
  layout: Record<string, unknown>
  [key: string]: unknown
}

export const DEFAULT_WIDGET_CONFIG: WidgetConfig = {
  name: 'My UGC gallery',
  view: 'grid',
  source: 'instagram',
  sourceHandle: '',
  columns: 4,
  mobileColumns: 2,
  spacing: 10,
  pagination: 'pagination',
  accent: '#CDEB45',
}

function clonePlain<T>(value: T): T {
  return JSON.parse(JSON.stringify(value)) as T
}

function toBaseOrigin(baseUrl?: string) {
  const fallback = typeof window !== 'undefined' ? window.location.origin : ''
  return baseUrl ?? fallback
}

function toAbsUrl(url: string | undefined, baseOrigin: string) {
  if (!url) return ''
  if (url.startsWith('http://') || url.startsWith('https://') || url.startsWith('blob:')) return url
  if (url.startsWith('/') && baseOrigin) return `${baseOrigin}${url}`
  return url
}

function samplePostsToWidgetPosts(rawPosts: any[], basePosts: WidgetEnginePost[], baseOrigin: string) {
  return (rawPosts || []).map((p: any, idx: number) => {
    const template = clonePlain((basePosts[idx % Math.max(basePosts.length, 1)] ?? {}) as WidgetEnginePost)
    const isVideo = Boolean(p.videoSrc || (p.mediaType && String(p.mediaType).toLowerCase().includes('video')))
    const rawImage = p.image || p.thumbnail || p.mediaUrl || ''
    const rawVideoUrl = p.videoSrc || p.mediaUrl || ''
    const image = toAbsUrl(rawImage, baseOrigin)
    const videoUrl = toAbsUrl(rawVideoUrl, baseOrigin)
    const likes = Number(p.likes ?? template.like_count ?? template.likes ?? 124)
    const comments = Number(p.comments ?? template.comments_count ?? template.comment ?? 18)
    const authorHandle = p.user
      ? String(p.user).replace(/^@/, '')
      : p.username
        ? String(p.username).replace(/^@/, '')
        : typeof template.username === 'string'
          ? String(template.username).replace(/^@/, '')
          : 'creator'
    const templateProducts = Array.isArray(template.products) ? template.products : []
    const products = (p.hotspots || []).map((h: any, hIdx: number) => ({
      ...(typeof templateProducts[hIdx] === 'object' ? (templateProducts[hIdx] as Record<string, unknown>) : {}),
      id: h.productId || `prod-${idx}-${hIdx}`,
      id_from_product: h.productId || `prod-${idx}-${hIdx}`,
      title: h.title || 'Tagged Product',
      price: h.price ? String(h.price) : '49',
      image: toAbsUrl(h.image || rawImage, baseOrigin),
      x_position: String(h.x ?? 50),
      y_position: String(h.y ?? 50),
      url: '#',
    }))

    const mediaVersions =
      template.mediaVersions && typeof template.mediaVersions === 'object'
        ? {
            ...(template.mediaVersions as Record<string, unknown>),
            imageSmall: image,
            image: image,
            videoPoster: image,
          }
        : {
            imageSmall: image,
            image,
            videoPoster: image,
          }

    return {
      ...template,
      id: `preview-post-${idx}`,
      media_type: isVideo ? 'VIDEO' : 'IMAGE',
      mediaType: isVideo ? 'VIDEO' : 'IMAGE',
      media_url: isVideo ? videoUrl : image,
      mediaUrl: isVideo ? videoUrl : image,
      thumbnail_url: image,
      thumbnail: image,
      mediaVersions,
      permalink: p.originalUrl || p.permalink || (typeof template.permalink === 'string' ? template.permalink : 'https://instagram.com'),
      user_name: authorHandle,
      username: authorHandle,
      userid: authorHandle,
      caption: p.caption || (typeof template.caption === 'string' ? template.caption : ''),
      like_count: likes,
      likes: String(likes),
      comments_count: comments,
      comment: String(comments),
      source: typeof template.source === 'string' ? template.source : 'Instagram_business',
      products,
    }
  })
}

/** Translate the builder config into the `layout` shape the create-collection
 *  API (`POST /gallery-layouts`) accepts. Kept deliberately small: the app
 *  fills the rest of the defaults from its layout map on save. */
export function configToLayout(c: WidgetConfig) {
  return {
    galleryView: c.view,
    galleryInformation: {
      name: c.name,
      postPerPage: '14',
      columns: String(c.columns),
      mobileColumns: String(c.mobileColumns),
      pagination: c.pagination,
      postSpacing: String(c.spacing),
    },
    brandColorFont: { brandAccentColor: c.accent },
  }
}

/** Translate builder WidgetConfig + demo/real post items into the full
 *  `window.widgetData` + `window.content` payload structure expected by
 *  the idukki-ext widget engine when data-app="true". */
export function configToWidgetData(c: WidgetConfig, rawPosts: any[], baseUrl?: string) {
  const effectiveBase = toBaseOrigin(baseUrl)

  const posts = (rawPosts || []).map((p: any, idx: number) => {
    const isVideo = p.videoSrc || (p.mediaType && String(p.mediaType).toLowerCase().includes('video'))
    const rawImage = p.image || p.thumbnail || p.mediaUrl || ''
    const rawVideoUrl = p.videoSrc || p.mediaUrl || ''

    const image = toAbsUrl(rawImage, effectiveBase)
    const videoUrl = toAbsUrl(rawVideoUrl, effectiveBase)

    const products = (p.hotspots || []).map((h: any, hIdx: number) => ({
      id: h.productId || `prod-${idx}-${hIdx}`,
      id_from_product: h.productId || `prod-${idx}-${hIdx}`,
      title: h.title || 'Tagged Product',
      price: h.price ? String(h.price) : '49',
      image: toAbsUrl(h.image || rawImage, effectiveBase),
      x_position: String(h.x ?? 50),
      y_position: String(h.y ?? 50),
      url: '#'
    }))

    const authorHandle = p.user ? String(p.user).replace(/^@/, '') : (p.username ? String(p.username).replace(/^@/, '') : 'creator')

    return {
      id: `post-${idx}`,
      media_type: isVideo ? 'VIDEO' : 'IMAGE',
      mediaType: isVideo ? 'VIDEO' : 'IMAGE',
      media_url: isVideo ? videoUrl : image,
      mediaUrl: isVideo ? videoUrl : image,
      thumbnail_url: image,
      thumbnail: image,
      permalink: p.originalUrl || p.permalink || 'https://instagram.com',
      user_name: authorHandle,
      username: authorHandle,
      userid: authorHandle,
      caption: p.caption || '',
      like_count: p.likes || 124,
      likes: String(p.likes || 124),
      comments_count: p.comments || 18,
      comment: String(p.comments || 18),
      source: 'Instagram_business',
      products
    }
  })

  const layout = {
    galleryView: c.view,
    galleryInformation: {
      name: c.name || 'My UGC gallery',
      postPerPage: '14',
      columns: String(c.columns),
      mobileColumns: String(c.mobileColumns),
      pagination: c.pagination || 'pagination',
      postSpacing: String(c.spacing),
    },
    brandColorFont: {
      brandAccentColor: c.accent || '#CDEB45',
      brandButtonColor: c.accent || '#CDEB45',
      brandHeaderFontColor: '#000000',
      brandCardFontColor: '#000000',
      brandPlayIconColor: '#ffffff',
      brandPlayIconBgColor: c.accent || '#CDEB45',
    },
    gallerySettings: {
      showProducts: true,
      showModalProducts: true,
      showHotspotProducts: true,
      showPostCaption: true,
      height: '600',
      width: '100%',
      cardHoverEffect: 'zoom',
      poweredBy: true,
    },
    cardSettings: {
      cardBackgroundColor: '#ffffff',
      cardTextColor: '#000000',
      cardBorder: '8px',
      cardHeight: '380'
    },
    sliderSettings: {
      autoPlay: true,
      slider: true,
      arrowColor: c.accent || '#CDEB45'
    }
  }

  const payload = {
    id: 'builder-preview',
    business_id: 'builder-demo',
    data: posts,
    layout
  }

  return {
    widgetData: {
      result: payload
    },
    content: payload
  }
}

export function mergeBuilderConfigIntoWidgetData(
  basePayload: WidgetEnginePayload,
  c: WidgetConfig,
  rawPosts: any[],
  baseUrl?: string,
) {
  const payload = clonePlain(basePayload)
  const baseOrigin = toBaseOrigin(baseUrl)
  const basePosts = Array.isArray(payload.data) ? payload.data : []
  const layout = (payload.layout && typeof payload.layout === 'object'
    ? payload.layout
    : {}) as Record<string, any>

  payload.data = samplePostsToWidgetPosts(rawPosts, basePosts, baseOrigin)
  payload.layout = layout

  layout.galleryView = c.view
  layout.galleryInformation = {
    ...(layout.galleryInformation ?? {}),
    name: c.name || 'My UGC gallery',
    columns: String(c.columns),
    mobileColumns: String(c.mobileColumns),
    pagination: c.pagination || layout.galleryInformation?.pagination || 'pagination',
    postSpacing: String(c.spacing),
  }
  layout.brandColorFont = {
    ...(layout.brandColorFont ?? {}),
    brandAccentColor: c.accent || layout.brandColorFont?.brandAccentColor || '#CDEB45',
    brandButtonColor: c.accent || layout.brandColorFont?.brandButtonColor || '#132f1c',
    brandDotActiveColor: c.accent || layout.brandColorFont?.brandDotActiveColor || '#132f1c',
  }
  layout.gallerySettings = {
    ...(layout.gallerySettings ?? {}),
    showProducts: true,
    showModalProducts: true,
    showHotspotProducts: true,
    showPostCaption: true,
  }
  layout.productsSettings = {
    ...(layout.productsSettings ?? {}),
    tapToReveal: false,
    hotspotOpenStyle: layout.productsSettings?.hotspotOpenStyle ?? 'card',
    hotspotBeaconStyle: layout.productsSettings?.hotspotBeaconStyle ?? 'pulse',
    productDrawerStyle: layout.productsSettings?.productDrawerStyle ?? 'auto',
  }
  layout.sampleData = { enabled: true, industry: c.source === 'tiktok' ? 'beauty' : 'fashion' }

  return {
    widgetData: {
      result: payload,
    },
    content: payload,
  }
}

/** The HTML snippet a customer pastes on their storefront. GUID-only:
 *  every visual choice lives in the saved layout. */
export function buildEmbedSnippet(guid: string, opts?: { bguid?: string; lazy?: boolean }): string {
  const attrs = [
    'data-ugc="idukki"',
    `data-guid="${guid}"`,
    opts?.bguid ? `data-bguid="${opts.bguid}"` : '',
    opts?.lazy ? 'data-ugc-lazy="1"' : '',
  ]
    .filter(Boolean)
    .join(' ')
  return `<div ${attrs}></div>\n<script src="${WIDGET_LOADER_URL}" defer></script>`
}

/** A standalone hosted URL that renders one collection, used for a real
 *  live preview in an iframe without rebuilding the renderer. */
export function hostedPreviewUrl(
  guid: string,
  opts?: { bguid?: string; test?: boolean },
): string {
  const params = new URLSearchParams({ guid })
  if (opts?.bguid) params.set('bguid', opts.bguid)
  if (opts?.test) params.set('idk_env', 'test')
  return `${WIDGET_PREVIEW_BASE}/?${params.toString()}`
}

/** Host for rep-generated sales-proposal widget previews (app/[slug]/
 *  [proposalSlug]). Same Idukki default as WIDGET_PREVIEW_BASE; override
 *  independently when a proposal must pin a specific widget host. Mirrors
 *  the production leg of nextapp's helpers/PreviewUrlHelper.ts hostname
 *  branching (widget.idukki.io); override via env for staging. */
export const PROPOSAL_WIDGET_BASE =
  process.env.NEXT_PUBLIC_PROPOSAL_WIDGET_BASE ?? 'https://widget.idukki.io'

/** Build the live iframe src for one layoutRow on a sales-proposal page.
 *  Same `?guid=&bguid=` query shape as hostedPreviewUrl above, just against
 *  PROPOSAL_WIDGET_BASE instead of WIDGET_PREVIEW_BASE. The widget's
 *  layout/style is resolved server-side per guid (already branded by the
 *  dashboard before the proposal is shared) — no brand params needed here. */
export function proposalPreviewUrl(guid: string, bguid: string): string {
  const params = new URLSearchParams({ guid, bguid })
  return `${PROPOSAL_WIDGET_BASE}/?${params.toString()}`
}

/** Deep link into the app to finish setup (connect a real source, go
 *  live) carrying the builder's intent so the app can prefill. */
export function appSignupUrl(opts?: { source?: WidgetSource; ref?: string }): string {
  const params = new URLSearchParams({ ref: opts?.ref ?? 'marketing-widget-builder' })
  if (opts?.source) params.set('source', opts.source)
  return `${APP_URL}/register?${params.toString()}`
}
